export type RenderTelemetryMetadata = {
  renderId?: string;
  model?: string;
  provider?: string;
};

export type StructuredTelemetryError = {
  errorType?: string;
  errorMessage?: string;
};

export type FirstRenderFailureReason =
  | "timeout"
  | "model_error"
  | "user_abandon"
  | "quota"
  | "validation_error"
  | "unknown";

const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const URL_PATTERN = /https?:\/\/\S+/gi;
const QUERY_SECRET_PATTERN = /([?&](?:token|code|email|session|auth)[^=]{0,20}=)[^&\s]+/gi;
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._-]+\b/gi;
const LONG_SECRET_PATTERN = /\b[a-f0-9]{32,}\b/gi;
const HTML_TAG_PATTERN = /<[^>\n]{1,200}>/g;
const MACHINE_ERROR_CODE_PATTERN = /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/i;

const normalizeSnakeCase = (value?: string): string | undefined => {
  if (!value) {
    return;
  }

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return normalized || undefined;
};

const containsSensitiveText = (value: string): boolean =>
  [
    EMAIL_PATTERN,
    URL_PATTERN,
    QUERY_SECRET_PATTERN,
    BEARER_PATTERN,
    LONG_SECRET_PATTERN,
  ].some((pattern) => {
    pattern.lastIndex = 0;
    return pattern.test(value);
  });

export const sanitizeTelemetryErrorType = (errorType?: string): string | undefined => {
  if (!errorType) {
    return;
  }

  const trimmed = errorType.trim();
  if (!trimmed || trimmed.length > 64 || containsSensitiveText(trimmed)) {
    return;
  }

  if (!MACHINE_ERROR_CODE_PATTERN.test(trimmed)) {
    return;
  }

  return normalizeSnakeCase(trimmed);
};

export function sanitizeTelemetryErrorMessage(errorMessage?: string): string | undefined {
  if (!errorMessage) {
    return;
  }

  const sanitized = errorMessage
    .replace(QUERY_SECRET_PATTERN, "$1[redacted]")
    .replace(EMAIL_PATTERN, "[redacted-email]")
    .replace(BEARER_PATTERN, "[redacted-secret]")
    .replace(URL_PATTERN, "[redacted-url]")
    .replace(LONG_SECRET_PATTERN, "[redacted-secret]")
    .replace(HTML_TAG_PATTERN, "[redacted-html]")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return sanitized.slice(0, 200) || undefined;
}

export function normalizeFirstRenderFailureReason({
  errorType,
  errorMessage,
}: StructuredTelemetryError): FirstRenderFailureReason {
  const errorTypeValue = sanitizeTelemetryErrorType(errorType);
  const message = (errorMessage || "").toLowerCase();
  const haystack = `${errorTypeValue || ""} ${message}`;

  if (
    haystack.includes("abort") ||
    haystack.includes("cancelled") ||
    haystack.includes("canceled") ||
    haystack.includes("user_abandon")
  ) {
    return "user_abandon";
  }

  if (haystack.includes("timeout") || haystack.includes("timed out") || haystack.includes("too long")) {
    return "timeout";
  }

  if (
    haystack.includes("rate_limit") ||
    haystack.includes("rate limited") ||
    haystack.includes("quota") ||
    haystack.includes("too many requests") ||
    haystack.includes("insufficient_credits") ||
    haystack.includes("credits")
  ) {
    return "quota";
  }

  if (
    haystack.includes("validation") ||
    haystack.includes("invalid") ||
    haystack.includes("required") ||
    haystack.includes("missing") ||
    haystack.includes("unsupported")
  ) {
    return "validation_error";
  }

  if (
    haystack.includes("provider_unavailable") ||
    haystack.includes("generation_failed") ||
    haystack.includes("model") ||
    haystack.includes("provider") ||
    haystack.includes("network") ||
    haystack.includes("fetch")
  ) {
    return "model_error";
  }

  return "unknown";
}

export const toCorrelationPayload = ({ renderId, model, provider }: RenderTelemetryMetadata) => ({
  ...(renderId ? { render_id: renderId } : {}),
  ...(model ? { model } : {}),
  ...(provider ? { provider } : {}),
});
